<template>
  <SettingsLayout>
    <div class="settings-content">
      <div class="heading">
        <h1 class="h2">General</h1>
      </div>
      <div class="block">
        <div class="mxw">
          <h2 class="h3">Your Workspaces</h2>
          <div
            v-for="(workspace, index) in workspaces"
            :key="workspace.workspaceUUID"
            class="input-row"
          >
            <h3>Workspace {{ index + 1 }}</h3>
            <div class="form-input">
              <input
                type="text"
                aria-label="Workspace name"
                v-model="workspace.name"
                @blur="updateWorkspaceName(workspace)"
                @keyup.enter="updateWorkspaceName(workspace)"
              />
              <!-- Never offered for the only workspace: the server deletes
                   every file in it and has no last-workspace guard. -->
              <a
                v-if="workspaces.length > 1"
                href="#"
                class="delete"
                :aria-label="`Delete ${workspace.name}`"
                @click.prevent="requestDeleteWorkspace(workspace)"
              >
                <i class="icon-delete"></i>
              </a>
            </div>
          </div>
          <div class="btn-wrap">
            <a
              href="#"
              class="btn btn-secondary-outline"
              :class="{ disabled: !canCreateWorkspace }"
              @click.prevent="handleAddWorkspaceClick"
            >
              <i class="icon-plus"></i>Add a New Workspace
              <span v-if="!canCreateWorkspace" style="margin-left: 5px">
                <span v-if="isSubscribed">(Limit: 3)</span>
                <span v-else>(Upgrade for more)</span>
              </span>
            </a>
            <div
              v-if="!isSubscribed && workspaces.length >= 1"
              class="upgrade-note"
            >
              <router-link to="/settings/plans" class="upgrade-link">
                Upgrade to Pro for up to 3 workspaces
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ConfirmDialog
      :show="!!pendingDelete"
      title="Delete workspace?"
      confirm-text="Delete workspace"
      :is-loading="isDeletingWorkspace"
      @confirm="confirmDeleteWorkspace"
      @cancel="cancelDeleteWorkspace"
    >
      <p class="confirm-message">
        <strong>{{ pendingDelete?.name }}</strong>
      </p>
      <p class="confirm-message">
        Every file and transcript in this workspace will be deleted. This can't
        be undone.
      </p>
    </ConfirmDialog>
  </SettingsLayout>
</template>

<script>
import SettingsLayout from "@/components/SettingsLayout.vue";
import ConfirmDialog from "@/components/popups/ConfirmDialog.vue";
import {
  getUserWorkspaces,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
} from "@/services/workspace.service";
import { useAppStore } from "@/stores/app-store";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { mapStores } from "pinia";
import { analytics } from "@/services/analytics.service";

// The workspace service helpers resolve to { data, error } — they do NOT
// throw on an HTTP error, so a failed call must be checked explicitly.
function assertOk(response, fallbackMessage) {
  if (!response || response.error) {
    throw new Error(response?.error?.message || fallbackMessage);
  }
  return response;
}

// Why a workspace name can't be saved, or null when it can.
function nameProblem(name) {
  if (!name.trim()) return "Workspace name cannot be empty.";
  if (!/^[a-zA-Z0-9\s]+$/.test(name)) {
    return "Workspace name invalid. Only alphanumeric characters and spaces are allowed.";
  }
  return null;
}

export default {
  name: "SettingsWorkspacesView",
  components: {
    SettingsLayout,
    ConfirmDialog,
  },
  computed: {
    ...mapStores(useAppStore),
    ...mapStores(useWorkSpaceStore),
    isSubscribed() {
      return this.workspaceStore.isSubscribedState;
    },
    canCreateWorkspace() {
      return this.workspaceStore.canCreateWorkspace;
    },
  },
  data() {
    return {
      workspaces: [],
      // Last name the server confirmed for each workspace. The inputs edit
      // names in place, so a failed rename restores from here.
      savedNames: {},
      // Workspace awaiting delete confirmation (null when the dialog is closed).
      pendingDelete: null,
      isDeletingWorkspace: false,
    };
  },
  created() {
    // The rename request in flight per workspace (plain, not reactive).
    this.renamesInFlight = {};
  },
  mounted() {
    this.loadWorkspaces();
    analytics.settingsWorkspacesViewed({
      workspace_count: this.workspaces.length,
    });
  },
  methods: {
    async loadWorkspaces() {
      try {
        const { data } = assertOk(await getUserWorkspaces(), "Load failed");
        if (!Array.isArray(data)) throw new Error("No workspaces returned");
        this.workspaces = data;
        this.savedNames = Object.fromEntries(
          data.map((ws) => [ws.workspaceUUID, ws.name]),
        );
      } catch (error) {
        this.$toast.warning(`Failed to load workspaces.`);
      }
    },
    async updateWorkspaceName(workspace) {
      const uuid = workspace.workspaceUUID;
      // Enter and the blur that follows both land here. One rename per
      // workspace at a time: a later call waits for the one in flight, then
      // compares against what the server now has.
      while (this.renamesInFlight[uuid]) {
        await this.renamesInFlight[uuid];
      }
      const savedName = this.savedNames[uuid];
      if (workspace.name === savedName) return;

      const problem = nameProblem(workspace.name);
      if (problem) {
        // The input edits the name in place; show what's actually saved.
        if (savedName !== undefined) workspace.name = savedName;
        this.$toast.warning(problem);
        return;
      }

      const rename = this.submitRename(workspace, savedName);
      this.renamesInFlight[uuid] = rename;
      try {
        await rename;
      } finally {
        delete this.renamesInFlight[uuid];
      }
    },
    async submitRename(workspace, savedName) {
      const uuid = workspace.workspaceUUID;
      const name = workspace.name;
      analytics.workspaceSettingsRenameClicked({ workspace_uuid: uuid });

      try {
        assertOk(await updateWorkspace(uuid, name), "Rename failed");
      } catch (error) {
        // Put the saved name back, unless the user has typed on since.
        if (savedName !== undefined && workspace.name === name) {
          workspace.name = savedName;
        }
        this.$toast.warning(`Failed to update workspace name`);
        return;
      }

      this.savedNames[uuid] = name;
      // Apply it to the store directly: the navbar switcher reads the store,
      // and fetchWorkspaces() swallows its own failures.
      this.workspaceStore.renameWorkspace(uuid, name);
      analytics.workspaceSettingsRenamed({
        workspace_uuid: uuid,
        new_name: name,
      });
      this.$toast.success(`Successfully updated workspace name`);
      await this.workspaceStore.fetchWorkspaces();
    },
    requestDeleteWorkspace(workspace) {
      if (this.workspaces.length <= 1) return;
      analytics.workspaceSettingsDeleteClicked({
        workspace_uuid: workspace.workspaceUUID,
      });
      this.pendingDelete = workspace;
    },
    cancelDeleteWorkspace() {
      if (this.isDeletingWorkspace) return;
      this.pendingDelete = null;
    },
    async confirmDeleteWorkspace() {
      if (!this.pendingDelete) return;
      this.isDeletingWorkspace = true;
      try {
        await this.deleteWorkspace(this.pendingDelete.workspaceUUID);
      } finally {
        this.isDeletingWorkspace = false;
        this.pendingDelete = null;
      }
    },
    async deleteWorkspace(workspaceUUID) {
      // The server soft-deletes every file in the workspace and has no
      // last-workspace guard — so the guard lives here (and in the native
      // apps). Deleting the only workspace would empty the whole library.
      if (this.workspaces.length <= 1) {
        this.$toast.warning("You need at least one workspace.");
        return;
      }

      try {
        assertOk(await deleteWorkspace(workspaceUUID), "Delete failed");
      } catch (error) {
        // The server refuses too: this list can be out of date (e.g. after a
        // delete in a mobile app).
        this.$toast.warning(
          error.message === "cannot_delete_last_workspace"
            ? "You need at least one workspace."
            : `Failed to delete workspace`,
        );
        return;
      }

      this.workspaces = this.workspaces.filter(
        (ws) => ws.workspaceUUID !== workspaceUUID,
      );
      analytics.workspaceSettingsDeleted({
        workspace_uuid: workspaceUUID,
      });
      this.$toast.success(`Successfully deleted workspace`);
      // Apply it to the store directly (fetchWorkspaces() swallows its own
      // failures); this also moves off the deleted workspace if it was active.
      this.workspaceStore.removeWorkspace(workspaceUUID);
      await this.workspaceStore.fetchWorkspaces();
    },
    handleAddWorkspaceClick() {
      if (this.canCreateWorkspace) {
        this.addNewWorkspace();
      } else {
        this.warnWorkspaceLimit();
      }
    },
    // The plan's workspace limit: free = 1, Pro = 3.
    warnWorkspaceLimit() {
      if (this.isSubscribed) {
        this.$toast.warning("Maximum limit of 3 workspaces reached.");
      } else {
        this.$toast.warning(
          "Free plan limited to 1 workspace. Please upgrade for up to 3 workspaces.",
        );
      }
    },
    async addNewWorkspace() {
      // Check workspace limit based on subscription status
      if (!this.canCreateWorkspace) {
        this.warnWorkspaceLimit();
        return;
      }

      let created;
      try {
        const newWorkspaceName = `Workspace${this.workspaces.length + 1}`;
        const response = assertOk(
          await createWorkspace(newWorkspaceName, "PRIVATE"),
          "Create failed",
        );
        if (!response.data) throw new Error("Create returned no workspace");
        created = response.data;
      } catch (error) {
        // The server enforces the limit too; the count here can be out of
        // date (e.g. a workspace created in a mobile app).
        if (error.message === "workspace_limit_reached") {
          this.warnWorkspaceLimit();
        } else {
          this.$toast.warning(`Failed to add new workspace`);
        }
        return;
      }

      this.workspaces.push(created);
      this.savedNames[created.workspaceUUID] = created.name;
      this.$toast.success(`Successfully added new workspace`);
      // canCreateWorkspace (the 3-workspace limit) and the navbar switcher
      // read the store: apply it there directly, since fetchWorkspaces()
      // swallows its own failures.
      this.workspaceStore.addWorkspace(created);
      await this.workspaceStore.fetchWorkspaces();
    },
  },
};
</script>

<style scoped>
#email-address {
  background-color: lightgrey;
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.upgrade-note {
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
}

.upgrade-link {
  color: #f16622;
  text-decoration: underline;
  font-weight: 500;
}

.upgrade-link:hover {
  text-decoration: none;
}
</style>
